import { h, createContext } from 'preact';
import { useState, useContext, useCallback } from 'preact/hooks';

const LayoutCtx = createContext(null);

/**
 * Provides persistent layout state (group open/closed) that survives
 * element selection changes within the same panel session.
 */
export function LayoutProvider({ children }) {
  const [state, setState] = useState({});

  const getLayoutState = useCallback((path, defaultValue) => {
    let current = state;
    for (const key of path) {
      if (current == null || typeof current !== 'object') return defaultValue;
      current = current[key];
    }
    return current !== undefined ? current : defaultValue;
  }, [state]);

  const setLayoutState = useCallback((path, value) => {
    setState(prev => {
      const next = { ...prev };
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        obj[key] = obj[key] != null ? { ...obj[key] } : {};
        obj = obj[key];
      }
      obj[path[path.length - 1]] = value;
      return next;
    });
  }, []);

  return (
    <LayoutCtx.Provider value={{ getLayoutState, setLayoutState }}>
      {children}
    </LayoutCtx.Provider>
  );
}

/**
 * Hook to read/write a piece of layout state.
 * @param {string[]} path - e.g. ['groups', 'action', 'open']
 * @param {*} defaultValue
 * @returns {[*, Function]}
 */
export function useLayoutState(path, defaultValue) {
  const ctx = useContext(LayoutCtx);
  if (!ctx) return [defaultValue, () => {}];
  const value = ctx.getLayoutState(path, defaultValue);
  const setValue = (v) => ctx.setLayoutState(path, v);
  return [value, setValue];
}
